"use client";

import { InputGroupAddon } from "@/components/ui/input-group";
import {
  type AnalysisTarget,
  getAnalysisTargetKey,
} from "@/lib/ai/analysis-target";
import { AnalysisTargetSelectedBadge } from "./analysis-target-display";

type AnalysisTargetSelectedContextsProps = {
  pageTarget: AnalysisTarget | null;
  manualTargets: AnalysisTarget[];
  mentionedTargets: AnalysisTarget[];
  onRemoveTarget: (target: AnalysisTarget) => void;
  displayMode?: "full" | "summary";
};

/**
 * 入力欄上部に、現在の送信 scope に効く page/manual/mention コンテキストを描画する。
 */
export function AnalysisTargetSelectedContexts({
  pageTarget,
  manualTargets,
  mentionedTargets,
  onRemoveTarget,
  displayMode = "full",
}: AnalysisTargetSelectedContextsProps) {
  const seenTargetKeys = new Set<string>();
  const displayTargets = [
    pageTarget,
    ...manualTargets,
    ...mentionedTargets,
  ].filter((target): target is AnalysisTarget => {
    if (!target) {
      return false;
    }
    const targetKey = getAnalysisTargetKey(target);
    if (seenTargetKeys.has(targetKey)) {
      return false;
    }
    seenTargetKeys.add(targetKey);
    return true;
  });

  if (displayTargets.length === 0) {
    return null;
  }

  if (displayMode === "summary") {
    const [primaryTarget, ...secondaryTargets] = displayTargets;

    if (!primaryTarget) {
      return null;
    }

    return (
      <InputGroupAddon align="block-start" className="w-full pb-0.5">
        <div className="flex w-full min-w-0 items-center gap-1 overflow-x-auto pr-1">
          <AnalysisTargetSelectedBadge
            target={primaryTarget}
            removable={false}
          />
          {secondaryTargets.length > 0 ? (
            <span className="inline-flex h-6 shrink-0 items-center rounded-full border bg-background px-2.5 text-muted-foreground text-xs">
              +{secondaryTargets.length}
            </span>
          ) : null}
        </div>
      </InputGroupAddon>
    );
  }

  return (
    <InputGroupAddon align="block-start" className="w-full pb-0.5">
      <div className="flex w-full min-w-0 items-center gap-0.5 overflow-x-auto pr-1">
        {displayTargets.map((target) => {
          const targetKey = getAnalysisTargetKey(target);
          return (
            <AnalysisTargetSelectedBadge
              key={targetKey}
              target={target}
              onRemove={() => onRemoveTarget(target)}
            />
          );
        })}
      </div>
    </InputGroupAddon>
  );
}
